// src/services/roomService.js
import { tenantDataService } from './tenantDataService';

const COLLECTION_NAME = 'rooms';

/**
 * Retrieve all rooms
 * @returns {Promise<Array<{ id: string; name: string; capacity: number; status?: string; }>>}
 */
export async function getRooms() {
  try {
    const rooms = await tenantDataService.getAll(COLLECTION_NAME);
    return rooms || [];
  } catch (error) {
    console.error('Error loading rooms:', error);
    return [];
  }
}

/**
 * Get a single room by ID
 * @param {string} id - Room ID
 * @returns {Promise<Object|null>}
 */
export async function getRoomById(id) {
  try {
    return await tenantDataService.getById(COLLECTION_NAME, id);
  } catch (error) {
    console.error('Error loading room:', error);
    return null;
  }
}

/**
 * Create a new room
 * @param {Object} roomData - Room data
 * @returns {Promise<Object>}
 */
export async function createRoom(roomData) {
  try {
    const room = {
      ...roomData,
      id: roomData.id || Date.now().toString(),
      status: roomData.status || 'available',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    await tenantDataService.create(COLLECTION_NAME, room);
    return room;
  } catch (error) {
    console.error('Error creating room:', error);
    throw error;
  }
}

/**
 * Update an existing room
 * @param {string} id - Room ID
 * @param {Object} updates - Room updates
 * @returns {Promise<Object>}
 */
export async function updateRoom(id, updates) {
  try {
    const updatedRoom = {
      ...updates,
      id,
      updatedAt: new Date().toISOString()
    };
    
    await tenantDataService.update(COLLECTION_NAME, id, updatedRoom);
    return updatedRoom;
  } catch (error) {
    console.error('Error updating room:', error);
    throw error;
  }
}

/**
 * Delete a room
 * @param {string} id - Room ID
 * @returns {Promise<boolean>}
 */
export async function deleteRoom(id) {
  try {
    await tenantDataService.delete(COLLECTION_NAME, id);
    return true;
  } catch (error) {
    console.error('Error deleting room:', error);
    return false;
  }
}

/**
 * Save multiple rooms (legacy compatibility)
 * @param {Array} rooms - Array of rooms
 * @returns {Promise<void>}
 * @deprecated Use individual CRUD operations instead
 */
export async function saveRooms(rooms) {
  try {
    // For backward compatibility, we'll update each room individually
    const promises = rooms.map(room => {
      if (room.id) {
        return updateRoom(room.id, room);
      } else {
        return createRoom(room);
      }
    });
    
    await Promise.all(promises);
  } catch (error) {
    console.error('Error saving rooms:', error);
    throw error;
  }
}

/**
 * Check room availability for a specific time slot
 * @param {string} roomId - Room ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} startTime - Start time
 * @param {string} endTime - End time
 * @param {string} excludeAppointmentId - Appointment ID to exclude from check
 * @returns {Promise<boolean>}
 */
export async function isRoomAvailable(roomId, date, startTime, endTime, excludeAppointmentId = null) {
  try {
    // This would need appointment service integration
    // For now, return true (will implement in next step)
    return true;
  } catch (error) {
    console.error('Error checking room availability:', error);
    return false;
  }
}

/**
 * Get room occupancy for a specific date
 * @param {string} roomId - Room ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<Array>}
 */
export async function getRoomOccupancy(roomId, date) {
  try {
    // This would need appointment service integration
    // For now, return empty array (will implement in next step)
    return [];
  } catch (error) {
    console.error('Error getting room occupancy:', error);
    return [];
  }
}
